import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getPanelElement, getPanelGroupElement } from 'react-resizable-panels';

import { LayoutArea, PanelConfig } from '../types';
import { AutoLayoutConfig, AutoLayoutStrategy, LayoutPattern, LayoutStructure } from '../utils/layoutStrategies';

/**
 * Hook that provides automatic layout strategy detection and management
 * Uses empirical measurements from react-resizable-panels for foolproof operation
 */
export function useAutoLayoutStrategy(visiblePanels: LayoutArea[], panelConfigs: Record<LayoutArea, Partial<PanelConfig>>, config: AutoLayoutConfig = {}) {
  const [viewport, setViewport] = useState({ width: 1200, height: 800 });
  const [currentStructure, setCurrentStructure] = useState<LayoutStructure | null>(null);
  const [empiricalMetrics, setEmpiricalMetrics] = useState<Record<string, any>>({});

  // Refs for tracking panel groups and elements
  const panelGroupRefs = useRef<Map<string, HTMLElement>>(new Map());
  const panelElementRefs = useRef<Map<LayoutArea, HTMLElement>>(new Map());
  const measurementCache = useRef<Map<string, { timestamp: number; data: any }>>(new Map());

  // Update viewport dimensions using native browser APIs
  useEffect(() => {
    const updateViewport = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  // Generate layout structure when panels or config changes
  const layoutPlan = useMemo(() => {
    if (visiblePanels.length === 0) return null;

    const plan = AutoLayoutStrategy.planLayout(visiblePanels, config, viewport);

    if (process.env.NODE_ENV === 'development') {
      console.group('🎯 Auto Layout Strategy');
      console.log('Detected Pattern:', plan.debugInfo.detectedPattern);
      console.log('Reason:', plan.debugInfo.reason);
      console.log('Structure:', plan.structure);
      console.log('Alternatives:', plan.debugInfo.alternatives);
      console.groupEnd();
    }

    return plan;
  }, [visiblePanels, config, viewport]);

  // Update current structure in a separate effect to avoid infinite loops
  useEffect(() => {
    if (layoutPlan?.structure) {
      setCurrentStructure(layoutPlan.structure);
    }
  }, [layoutPlan]);

  // Empirical measurement system using react-resizable-panels native methods
  const measurePanelGroup = useCallback((groupId: string): { width: number; height: number } | null => {
    const element = getPanelGroupElement(groupId);
    if (!element) return null;

    const rect = element.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
    };
  }, []);

  const measurePanel = useCallback((panelId: LayoutArea): { width: number; height: number; percentage: number } | null => {
    const panelElement = getPanelElement(panelId);
    if (!panelElement) return null;

    const panelRect = panelElement.getBoundingClientRect();

    // Find parent panel group to calculate percentage
    const parentGroup = panelElement.closest('[data-panel-group]') as HTMLElement;
    if (!parentGroup) return null;

    const parentRect = parentGroup.getBoundingClientRect();
    const isHorizontal = parentGroup.getAttribute('data-direction') === 'horizontal';

    const percentage = isHorizontal ? Math.round((panelRect.width / parentRect.width) * 100) : Math.round((panelRect.height / parentRect.height) * 100);

    return {
      width: panelRect.width,
      height: panelRect.height,
      percentage: Math.max(0, Math.min(100, percentage)),
    };
  }, []);

  // Cached measurement system to avoid excessive DOM queries
  const getCachedMeasurement = useCallback((key: string, measureFn: () => any, ttl: number = 100): any => {
    const now = Date.now();
    const cached = measurementCache.current.get(key);

    if (cached && now - cached.timestamp < ttl) {
      return cached.data;
    }

    const data = measureFn();
    measurementCache.current.set(key, { timestamp: now, data });
    return data;
  }, []);

  // Real-time empirical size validation
  const validateCurrentSizes = useCallback((): {
    isValid: boolean;
    violations: Array<{ panel: LayoutArea; issue: string; actual: number; expected: number }>;
    totalPercentage: number;
  } => {
    if (!currentStructure) return { isValid: true, violations: [], totalPercentage: 100 };

    const violations: Array<{ panel: LayoutArea; issue: string; actual: number; expected: number }> = [];
    let totalPercentage = 0;

    currentStructure.nestedGroups.forEach((group) => {
      let groupTotal = 0;

      group.panels.forEach((panelId) => {
        const measurement = getCachedMeasurement(
          `panel-${panelId}`,
          () => measurePanel(panelId),
          50, // 50ms cache for active measurements
        );

        if (measurement) {
          const config = panelConfigs[panelId];
          groupTotal += measurement.percentage;

          // Validate against config constraints
          if (config?.minPixels && measurement.width < config.minPixels) {
            violations.push({
              panel: panelId,
              issue: 'Below minimum width',
              actual: measurement.width,
              expected: config.minPixels,
            });
          }

          if (config?.maxPixels && measurement.width > config.maxPixels) {
            violations.push({
              panel: panelId,
              issue: 'Above maximum width',
              actual: measurement.width,
              expected: config.maxPixels,
            });
          }
        }
      });

      // Validate group doesn't exceed 100%
      if (groupTotal > 105) {
        // Allow 5% tolerance for browser rounding
        violations.push({
          panel: group.panels[0], // Representative panel
          issue: 'Group exceeds 100%',
          actual: groupTotal,
          expected: 100,
        });
      }

      totalPercentage = Math.max(totalPercentage, groupTotal);
    });

    return {
      isValid: violations.length === 0,
      violations,
      totalPercentage,
    };
  }, [currentStructure, panelConfigs, getCachedMeasurement, measurePanel]);

  // Auto-correction system for layout violations
  const autoCorrectLayout = useCallback(
    (violations: any[]) => {
      if (violations.length === 0) return;

      // Group violations by type
      const sizeViolations = violations.filter((v) => v.issue.includes('width') || v.issue.includes('height'));
      const overflowViolations = violations.filter((v) => v.issue.includes('exceeds'));

      // Correct size violations by triggering panel auto-sizing
      sizeViolations.forEach((violation) => {
        const config = panelConfigs[violation.panel as LayoutArea];
        if (config?.autoSize === 'content') {
          // Trigger content-based resize
          const panelElement = getPanelElement(violation.panel);
          if (panelElement) {
            // Dispatch custom event for intelligent resize
            panelElement.dispatchEvent(
              new CustomEvent('panel-auto-resize', {
                detail: { reason: 'constraint-violation', violation },
              }),
            );
          }
        }
      });

      // Log corrections for development
      if (process.env.NODE_ENV === 'development') {
        console.warn('🔧 Auto Layout Corrections Applied:', violations);
      }
    },
    [panelConfigs],
  );

  // Continuous monitoring system
  useEffect(() => {
    if (!currentStructure) return;

    const monitoringInterval = setInterval(() => {
      const validation = validateCurrentSizes();

      if (!validation.isValid) {
        autoCorrectLayout(validation.violations);
      }

      // Only update state if there are meaningful changes
      setEmpiricalMetrics((prev) => {
        const newMetrics = {
          lastValidation: Date.now(),
          isValid: validation.isValid,
          violations: validation.violations,
          totalPercentage: validation.totalPercentage,
        };

        // Prevent unnecessary re-renders by checking for actual changes
        if (
          prev.isValid === newMetrics.isValid &&
          prev.violations?.length === newMetrics.violations.length &&
          Math.abs((prev.totalPercentage || 100) - newMetrics.totalPercentage) < 1
        ) {
          return prev; // No meaningful change, keep previous state
        }

        return newMetrics;
      });
    }, 500); // Reduced frequency from 200ms to 500ms

    return () => clearInterval(monitoringInterval);
  }, [currentStructure, validateCurrentSizes, autoCorrectLayout]);

  // Panel group configuration generator
  const getPanelGroupConfig = useCallback(
    (groupId: string) => {
      if (!layoutPlan?.structure) return null;

      const group = layoutPlan.structure.nestedGroups.find((g) => g.id === groupId);
      if (!group) return null;

      // Calculate sizes using empirical data
      const groupMeasurement = getCachedMeasurement(`group-${groupId}`, () => measurePanelGroup(groupId), 100);

      const sizes = group.panels.map((panelId) => {
        const measurement = getCachedMeasurement(`panel-${panelId}`, () => measurePanel(panelId));

        if (measurement) {
          return measurement.percentage;
        }

        // Fallback to calculated size from layout plan
        const planMetrics = layoutPlan.metrics[groupId];
        const panelIndex = group.panels.indexOf(panelId);
        return planMetrics?.sizes[panelIndex] || 20;
      });

      return {
        id: groupId,
        direction: group.direction,
        panels: group.panels,
        defaultSizes: sizes,
        empiricalSize: groupMeasurement,
        order: group.order,
      };
    },
    [layoutPlan, getCachedMeasurement, measurePanelGroup, measurePanel],
  );

  // Memoize helper functions to prevent re-renders
  const isPatternActive = useCallback((pattern: LayoutPattern) => currentStructure?.pattern === pattern, [currentStructure]);
  const getGroupsForPattern = useCallback(() => currentStructure?.nestedGroups || [], [currentStructure]);
  const getSpanningPanels = useCallback(() => currentStructure?.spanningPanels || [], [currentStructure]);
  const getTotalLayoutPercentage = useCallback(() => empiricalMetrics.totalPercentage || 100, [empiricalMetrics.totalPercentage]);
  const getLayoutViolations = useCallback(() => empiricalMetrics.violations || [], [empiricalMetrics.violations]);
  const isLayoutValid = useCallback(() => empiricalMetrics.isValid !== false, [empiricalMetrics.isValid]);

  // Developer-friendly API
  const api = useMemo(
    () => ({
      // Current layout information
      pattern: currentStructure?.pattern || LayoutPattern.VERTICAL_STACK,
      structure: currentStructure,

      // Empirical measurement functions
      measurePanel,
      measurePanelGroup,
      validateCurrentSizes,

      // Configuration helpers
      getPanelGroupConfig,

      // Debug information
      debugInfo: layoutPlan?.debugInfo,
      empiricalMetrics,

      // Layout helpers
      isPatternActive,
      getGroupsForPattern,
      getSpanningPanels,

      // Real-time metrics
      getTotalLayoutPercentage,
      getLayoutViolations,
      isLayoutValid,
    }),
    [
      currentStructure,
      measurePanel,
      measurePanelGroup,
      validateCurrentSizes,
      getPanelGroupConfig,
      layoutPlan?.debugInfo,
      empiricalMetrics,
      isPatternActive,
      getGroupsForPattern,
      getSpanningPanels,
      getTotalLayoutPercentage,
      getLayoutViolations,
      isLayoutValid,
    ],
  );

  return api;
}

/**
 * Simple hook for developers who just want automatic layout without customization
 */
export function useSimpleAutoLayout(visiblePanels: LayoutArea[]) {
  const defaultConfigs = Object.fromEntries(visiblePanels.map((area) => [area, {}])) as Record<LayoutArea, Partial<PanelConfig>>;

  return useAutoLayoutStrategy(visiblePanels, defaultConfigs, {
    preferFullHeightSidebars: true,
  });
}

/**
 * Hook for advanced layout scenarios with full customization
 */
export function useAdvancedAutoLayout(visiblePanels: LayoutArea[], panelConfigs: Record<LayoutArea, Partial<PanelConfig>>, layoutConfig: AutoLayoutConfig) {
  return useAutoLayoutStrategy(visiblePanels, panelConfigs, layoutConfig);
}
